import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { authAPI, getHomeRoute } from '@/lib/api';
import { supabase } from '@/lib/supabaseClient';
import { enterFullscreenWithEscapeLock } from '@/lib/fullscreenUtils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/card';
import { toast } from 'sonner';
import { Store, Code2, Phone, Mail, AlertTriangle, Clock, Sun, Moon, Monitor, Wifi, Eye, EyeOff } from 'lucide-react';

const LOGIN_THEMES = ['light', 'system', 'dark'];

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLanguage();
  const { theme, setTheme } = useTheme();
  const ThemeIcon = theme === 'dark' ? Moon : theme === 'system' ? Monitor : Sun;
  const cycleTheme = () => setTheme(LOGIN_THEMES[(LOGIN_THEMES.indexOf(theme) + 1) % LOGIN_THEMES.length]);

  const [email, setEmail] = useState('');   // username or platform-admin email
  const [password, setPassword] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotUsername, setForgotUsername] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Read reason injected by the API interceptor on auth failures (suspended / expired)
  const authReason = new URLSearchParams(window.location.search).get('reason');

  // Dev mode — triple-click the logo reveals demo credentials
  const logoClickCount = useRef(0);
  const logoClickTimer = useRef(null);
  const [devMode, setDevMode] = useState(false);

  const handleLogoClick = () => {
    logoClickCount.current += 1;
    clearTimeout(logoClickTimer.current);
    logoClickTimer.current = setTimeout(() => { logoClickCount.current = 0; }, 1500);
    if (logoClickCount.current >= 3) {
      logoClickCount.current = 0;
      setDevMode(d => !d);
      toast.info(devMode ? 'Dev mode hidden' : '🛠 Dev mode — demo credentials revealed', { duration: 2000 });
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${window.location.origin}/auth/callback` },
      });
      if (error) toast.error(error.message || 'Google sign-in failed');
    } catch {
      toast.error('Google sign-in failed');
      setGoogleLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await authAPI.login({ email, password });
      const { mfa_required, mfa_enrolled, token, refresh_token, user: userData } = response.data;

      if (mfa_required) {
        // Store the pending user so MFA pages can call login() after verification
        sessionStorage.setItem('kiranaflow_pending_user', JSON.stringify(userData));
        // Gate token: MFARoute checks kiranaflow_token — store aal1 token in sessionStorage
        // so the /mfa/* routes are accessible. Replaced by the aal2 token after verification.
        sessionStorage.setItem('kiranaflow_token', token);
        if (response.data.session_id) localStorage.setItem('kiranaflow_session_id', response.data.session_id);
        // Sync Supabase JS client so supabase.auth.mfa.* calls work on the MFA pages
        await supabase.auth.setSession({ access_token: token, refresh_token });
        navigate(mfa_enrolled ? '/mfa/verify' : '/mfa/enroll');
      } else {
        // mfa_exempt user — proceed directly
        await login(response.data);
        toast.success('Welcome back!');
        navigate(getHomeRoute(userData?.role));
        enterFullscreenWithEscapeLock();
      }
    } catch (error) {
      if (!error.response) {
        toast.error('Cannot reach server. Check your connection or contact support.');
      } else {
        toast.error(error.response?.data?.detail || 'Login failed. Check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotUsername.trim()) return;
    setForgotLoading(true);
    try {
      await authAPI.forgotPassword({
        username: forgotUsername.trim(),
        redirect_to: `${window.location.origin}/auth/reset-password`,
      });
      setForgotSent(true);
    } catch {
      // Still show success — don't leak whether username exists
      setForgotSent(true);
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-amber-50 flex items-center justify-center p-4 relative">
      <button
        type="button"
        onClick={cycleTheme}
        aria-label="Toggle theme"
        className="absolute top-4 right-4 p-2 rounded-full text-muted-foreground hover:bg-black/5 transition-colors"
      >
        <ThemeIcon className="h-5 w-5" />
      </button>
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-4 text-center">
          <button
            type="button"
            onClick={handleLogoClick}
            className="mx-auto bg-primary text-primary-foreground w-16 h-16 rounded-full flex items-center justify-center cursor-pointer select-none focus:outline-none"
          >
            <Store className="h-8 w-8" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">{t('app.name')}</h1>
            <p className="text-muted-foreground mt-1 text-sm">Smart ERP for your kirana store</p>
          </div>
        </CardHeader>

        {authReason === 'suspended' && (
          <div className="mx-6 mb-0 flex items-start gap-3 rounded-lg bg-orange-50 border border-orange-200 px-4 py-3 text-sm text-orange-800">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5 text-orange-500" />
            <div>
              <p className="font-semibold">Account suspended</p>
              <p className="text-orange-700 text-xs mt-0.5">Your account has been suspended. Please contact support to restore access.</p>
            </div>
          </div>
        )}
        {authReason === 'expired' && (
          <div className="mx-6 mb-0 flex items-start gap-3 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
            <Clock className="h-4 w-4 flex-shrink-0 mt-0.5 text-red-500" />
            <div>
              <p className="font-semibold">Subscription expired</p>
              <p className="text-red-700 text-xs mt-0.5">Your subscription has expired. Please renew your plan to continue.</p>
            </div>
          </div>
        )}
        {authReason === 'session_revoked' && (
          <div className="mx-6 mb-0 flex items-start gap-3 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-800">
            <Wifi className="h-4 w-4 flex-shrink-0 mt-0.5 text-blue-500" />
            <div>
              <p className="font-semibold">Signed in on another device</p>
              <p className="text-blue-700 text-xs mt-0.5">Your session was ended because this account logged in on another device. Only one device is allowed at a time.</p>
            </div>
          </div>
        )}

        {forgotMode ? (
          <form onSubmit={handleForgotPassword}>
            <CardContent className="space-y-4">
              {forgotSent ? (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-4 text-center space-y-1">
                  <p className="text-sm font-semibold text-emerald-800">Check your email</p>
                  <p className="text-xs text-emerald-700">
                    If that username has a linked email, a password reset link has been sent. Check your inbox (and spam folder).
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="forgot-username">Your Username</Label>
                  <Input
                    id="forgot-username" type="text" placeholder="Enter your username"
                    value={forgotUsername} onChange={(e) => setForgotUsername(e.target.value)}
                    required autoFocus className="h-12"
                  />
                  <p className="text-xs text-muted-foreground">We'll send a reset link to the email linked to your account.</p>
                </div>
              )}
            </CardContent>
            <CardFooter className="flex flex-col gap-3">
              {!forgotSent && (
                <Button type="submit" className="w-full h-12 text-base font-bold" disabled={forgotLoading}>
                  {forgotLoading ? 'Sending…' : 'Send Reset Link'}
                </Button>
              )}
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => { setForgotMode(false); setForgotSent(false); setForgotUsername(''); }}
              >
                ← Back to sign in
              </button>
            </CardFooter>
          </form>
        ) : (
          <form onSubmit={handleLogin}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Username</Label>
                <Input
                  id="email" type="text" placeholder="Enter your username"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  required className="h-12"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">{t('auth.password')}</Label>
                  <button
                    type="button"
                    tabIndex={-1}
                    className="text-xs text-emerald-700 hover:text-emerald-800 font-medium transition-colors"
                    onClick={() => { setForgotMode(true); setForgotUsername(email); }}
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Input
                    id="password" type={showPassword ? 'text' : 'password'} placeholder="••••••••"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    required className="h-12 pr-10"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setShowPassword(v => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Dev mode — triple-click logo; credentials come from server env vars */}
              {devMode && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs space-y-1">
                  <div className="flex items-center gap-1.5 font-semibold text-amber-700 mb-1.5">
                    <Code2 className="h-3.5 w-3.5" /> Platform Admin Access
                  </div>
                  <div className="text-amber-800 px-2 py-1">
                    🛡 Enter platform admin credentials manually.
                  </div>
                </div>
              )}
            </CardContent>

            <CardFooter className="flex flex-col gap-3">
              <Button type="submit" className="w-full h-12 text-base font-bold" disabled={loading}>
                {loading ? 'Signing in…' : 'Sign In'}
              </Button>

              <div className="flex items-center gap-3 w-full">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-muted-foreground">or</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full h-12 gap-3 text-sm font-medium"
                onClick={handleGoogleLogin}
                disabled={googleLoading}
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                {googleLoading ? 'Redirecting…' : 'Sign in with Google'}
              </Button>
              {/* Contact us instead of register */}
              <div className="w-full rounded-lg bg-gray-50 border border-gray-100 px-4 py-3 text-center space-y-1.5">
                <p className="text-xs text-muted-foreground font-medium">Don't have an account?</p>
                <p className="text-xs text-muted-foreground">Contact us to get set up on KiranaFlow</p>
                <div className="flex flex-col gap-1 mt-1">
                  <a href="tel:+918888855212" className="flex items-center justify-center gap-1.5 text-xs text-emerald-700 hover:text-emerald-800 font-medium">
                    <Phone className="h-3 w-3" /> +91 88888 55212
                  </a>
                  <a href="mailto:rohankakad712@gmail.com" className="flex items-center justify-center gap-1.5 text-xs text-emerald-700 hover:text-emerald-800 font-medium">
                    <Mail className="h-3 w-3" /> rohankakad712@gmail.com
                  </a>
                </div>
              </div>

              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => navigate('/home')}
              >
                ← Back to homepage
              </button>
            </CardFooter>
          </form>
        )}
      </Card>
    </div>
  );
};

export default Login;
